/** Convert API value (0-1) to display value (0-100%). Returns null for nullish input. */
export const toDisplayValue = (apiValue: number | null | undefined): number | null => {
	if (apiValue == null) return null;
	return Math.round(apiValue * 1000) / 10; // 1 decimal place
};

/** Convert display value (0-100%) to API value (0-1). Returns null for nullish input. */
export const toApiValue = (displayValue: number | null | undefined): number | null => {
	if (displayValue == null) return null;
	return displayValue / 100;
};

interface FilterEntry {
	operator: string;
	value: string[] | string | null;
}

const NO_VALUE_OPERATORS = ['is empty', 'is not empty', 'isEmpty', 'isNotEmpty'];

/**
 * Convert a failure probability filter from display range (0-100) to API range (0-1).
 * Returns the filter entry unchanged for operators that carry no value.
 */
export const convertFilterToApiRange = (filter: FilterEntry): FilterEntry => {
	if (NO_VALUE_OPERATORS.includes(filter.operator)) return filter;
	if (filter.value == null || filter.value === '') return filter;

	return {
		...filter,
		value: Array.isArray(filter.value)
			? filter.value.filter(v => v !== '').map(v => toApiValue(Number(v))?.toString() ?? v)
			: toApiValue(Number(filter.value))?.toString() ?? filter.value,
	};
};

interface ApiAssetResult {
	asset_id: string;
	asset_group_id?: string;
	asset_group_name: string;
	failure_probability: number | null;
	age: number | null;
	rul: number | null;
	failure_year: number | null;
	historical_failure: string;
	grade?: string;
	material?: string | null;
	diameter?: number | null;
	installation_year?: number | null;
}

/**
 * Map an API asset result to the internal row representation used by the DataGrid.
 * Converts failure_probability from 0-1 to 0-100% display range.
 */
export const mapApiResultToRow = (result: ApiAssetResult) => ({
	id: result.asset_id,
	assetId: result.asset_id,
	assetGroupId: result.asset_group_id,
	assetGroupName: result.asset_group_name,
	age: result.age?.toFixed(1),
	failureProbability: toDisplayValue(result.failure_probability)?.toString(),
	rul: result.rul?.toFixed(1),
	failureYear: result.failure_year?.toString(),
	historicalFailure: result.historical_failure,
	grade: result.grade || undefined,
	material: result.material ?? null,
	diameter: result.diameter ?? null,
	installationYear: result.installation_year ?? null,
});
